import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import {
  coordsForPlacement,
  getAnchorElement,
  isTouchDevice,
  resolvePlacement,
  transformForPlacement,
  TOOLTIP_GAP,
  TOOLTIP_VIEWPORT_PAD,
  type TooltipCoords,
  type TooltipPosition,
} from '../components/ui/tooltipPlacement';

export type AnchoredTooltipTouchBehavior = 'off' | 'toggle';

export interface UseAnchoredTooltipOptions {
  position?: TooltipPosition;
  /** Hover/focus delay before show. */
  delay?: number;
  disabled?: boolean;
  /**
   * `off` — no hover/tap show on touch (action tooltips).
   * `toggle` — tap opens/closes (info tooltips).
   */
  touchBehavior?: AnchoredTooltipTouchBehavior;
  /** Extra space needed before auto-flip (taller bubbles). */
  flipNeed?: number;
  gap?: number;
  viewportPad?: number;
  /** Keeps wide bubbles on-screen horizontally. */
  bubbleHalfWidth?: number;
  /** Stable id override (InfoTooltip). */
  id?: string;
  /** Position near the mouse cursor instead of the trigger element. */
  followCursor?: boolean;
}

export interface UseAnchoredTooltipResult {
  isVisible: boolean;
  coords: TooltipCoords | null;
  tooltipId: string;
  wrapperRef: RefObject<HTMLDivElement>;
  isTouch: boolean;
  shouldShowOnHover: boolean;
  showTooltip: () => void;
  hideTooltip: () => void;
  /** Desktop: hide. Touch + toggle: open/close. */
  handleTriggerPointer: (e?: ReactMouseEvent | ReactTouchEvent) => void;
  /** When followCursor: keep bubble under the pointer. */
  handleMouseMove?: (e: ReactMouseEvent) => void;
  /** Records pointer (followCursor) then schedules show. */
  handleMouseEnter?: (e: ReactMouseEvent) => void;
  bubbleStyle: CSSProperties | undefined;
}

/**
 * Shared visibility + placement for Tooltip / InfoTooltip.
 */
export function useAnchoredTooltip(
  options: UseAnchoredTooltipOptions = {}
): UseAnchoredTooltipResult {
  const {
    position = 'bottom',
    delay = 700,
    disabled = false,
    touchBehavior = 'off',
    flipNeed = 40,
    gap,
    viewportPad,
    bubbleHalfWidth,
    id,
    followCursor = false,
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const justToggledRef = useRef(false);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const touchDeviceRef = useRef(isTouchDevice());
  const generatedId = useId();
  const tooltipId = id || generatedId;

  const isTouch = touchDeviceRef.current;
  const shouldShowOnHover = !isTouch;

  const clearShowTimeout = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const hideTooltip = useCallback(() => {
    clearShowTimeout();
    setIsVisible(false);
    setCoords(null);
    pointerRef.current = null;
  }, [clearShowTimeout]);

  const setCoordsFromPointer = useCallback(
    (x: number, y: number) => {
      const offset = gap ?? TOOLTIP_GAP;
      const pad = viewportPad ?? TOOLTIP_VIEWPORT_PAD;
      const placement = position;
      let top = y;
      let left = x;
      if (placement === 'bottom') {
        top = y + offset;
        left = x;
      } else if (placement === 'top') {
        top = y - offset;
        left = x;
      } else if (placement === 'right') {
        top = y;
        left = x + offset;
      } else {
        top = y;
        left = x - offset;
      }
      // Keep roughly on-screen (bubble size unknown; pad from edges)
      if (typeof window !== 'undefined') {
        left = Math.min(Math.max(left, pad), window.innerWidth - pad);
        top = Math.min(Math.max(top, pad), window.innerHeight - pad);
      }
      setCoords({ top, left, placement });
    },
    [gap, viewportPad, position]
  );

  const showTooltip = useCallback(() => {
    if (disabled) return;
    if (!shouldShowOnHover && touchBehavior === 'off') return;
    clearShowTimeout();
    const showDelay = isTouch && touchBehavior === 'toggle' ? 0 : delay;
    timeoutIdRef.current = setTimeout(() => {
      if (followCursor && pointerRef.current) {
        setCoordsFromPointer(pointerRef.current.x, pointerRef.current.y);
      }
      setIsVisible(true);
      timeoutIdRef.current = null;
    }, showDelay);
  }, [
    disabled,
    shouldShowOnHover,
    touchBehavior,
    isTouch,
    delay,
    clearShowTimeout,
    followCursor,
    setCoordsFromPointer,
  ]);

  const handleMouseMove = useCallback(
    (e: ReactMouseEvent) => {
      if (!followCursor || !shouldShowOnHover) return;
      pointerRef.current = { x: e.clientX, y: e.clientY };
      if (isVisible) {
        setCoordsFromPointer(e.clientX, e.clientY);
      }
    },
    [followCursor, shouldShowOnHover, isVisible, setCoordsFromPointer]
  );

  const handleMouseEnter = useCallback(
    (e: ReactMouseEvent) => {
      if (followCursor) {
        pointerRef.current = { x: e.clientX, y: e.clientY };
      }
      showTooltip();
    },
    [followCursor, showTooltip]
  );

  const handleTriggerPointer = useCallback(
    (e?: ReactMouseEvent | ReactTouchEvent) => {
      if (isTouch && touchBehavior === 'toggle') {
        e?.preventDefault();
        e?.stopPropagation();
        justToggledRef.current = true;
        window.setTimeout(() => {
          justToggledRef.current = false;
        }, 200);
        if (isVisible) {
          hideTooltip();
        } else {
          clearShowTimeout();
          setIsVisible(true);
        }
        return;
      }
      hideTooltip();
    },
    [isTouch, touchBehavior, isVisible, hideTooltip, clearShowTimeout]
  );

  useEffect(() => {
    if (!isVisible || !wrapperRef.current || followCursor) return;

    const updatePosition = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const targetEl = getAnchorElement(wrapper);
      const rect = targetEl.getBoundingClientRect();
      const placement = resolvePlacement(position, rect, flipNeed);
      setCoords(
        coordsForPlacement(placement, rect, { gap, viewportPad, bubbleHalfWidth })
      );
    };

    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', updatePosition, { passive: true, capture: true });
    window.addEventListener('resize', updatePosition, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', updatePosition, { capture: true });
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible, position, flipNeed, gap, viewportPad, bubbleHalfWidth, followCursor]);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        hideTooltip();
        if (touchBehavior === 'toggle' && wrapperRef.current) {
          wrapperRef.current.focus();
        }
      }
    };

    const handleOutside = (e: Event) => {
      if (touchBehavior === 'toggle' && isTouch) {
        if (justToggledRef.current) return;
        if (wrapperRef.current?.contains(e.target as Node)) return;
        hideTooltip();
        return;
      }
      hideTooltip();
    };

    document.addEventListener('keydown', handleKeyDown);

    if (touchBehavior === 'toggle' && isTouch) {
      document.addEventListener('touchstart', handleOutside, { passive: true });
      document.addEventListener('click', handleOutside);
    } else {
      document.addEventListener('touchstart', handleOutside, { passive: true, capture: true });
      document.addEventListener('click', handleOutside, { capture: true });
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (touchBehavior === 'toggle' && isTouch) {
        document.removeEventListener('touchstart', handleOutside);
        document.removeEventListener('click', handleOutside);
      } else {
        document.removeEventListener('touchstart', handleOutside, { capture: true });
        document.removeEventListener('click', handleOutside, { capture: true });
      }
    };
  }, [isVisible, hideTooltip, touchBehavior, isTouch]);

  useEffect(() => () => clearShowTimeout(), [clearShowTimeout]);

  const bubbleStyle =
    coords != null
      ? {
          top: `${coords.top}px`,
          left: `${coords.left}px`,
          transform: transformForPlacement(coords.placement),
        }
      : undefined;

  return {
    isVisible,
    coords,
    tooltipId,
    wrapperRef,
    isTouch,
    shouldShowOnHover,
    showTooltip,
    hideTooltip,
    handleTriggerPointer,
    handleMouseMove: followCursor ? handleMouseMove : undefined,
    handleMouseEnter: followCursor ? handleMouseEnter : undefined,
    bubbleStyle,
  };
}
